/**
 * @file index.jsx
 * @brief Renders the listing creation and edit form, including rich text, uploads, and sortable images.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { createApiClient } from "../../api/axios";
import { useUserProfile } from "../../context/UserProfileContext";
import { useDropzone } from "react-dropzone";
import AsyncSelect from "react-select/async";
import RichTextEditor from "../../components/RichTextEditor";
import {
    DndContext,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { useListingUpload } from "../../hooks/useListingUpload";
import { clampToMaxLength, FIELD_LIMITS } from "../../constants/fieldLimits";
import { stripHtmlTags } from "../../constants/fieldLimits";
import styles from "./listing-form.module.css";

/* ─── Sortable image tile ────────────────────────────────────────────────── */

function SortableImage({ image, index, onRemove, t }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: image.id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
            {...attributes}
            {...listeners}
            className={styles.imageCard}
        >
            {index === 0 && <span className={styles.coverBadge}>{t("listingForm.cover")}</span>}
            <img src={image.preview} alt="" />
            <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => onRemove(image.id)}
                className={styles.removeBtn}
                aria-label={t("listingForm.removeImage")}
            >
                ✕
            </button>
        </div>
    );
}

/* ─── Field wrapper ──────────────────────────────────────────────────────── */

function Field({ label, error, children }) {
    return (
        <div className={styles.field}>
            {label && <label className={styles.label}>{label}</label>}
            {children}
            {error && <p className={styles.errorMsg}>{error}</p>}
        </div>
    );
}

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function ListingForm({ mode = "create" }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const auth     = useAuth();
    const accessToken = auth.user?.access_token;
    const api      = useMemo(
        () => createApiClient({
            isAuthenticated: auth.isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, auth.isAuthenticated]
    );
    const { profile } = useUserProfile();

    const isEditMode = mode === "edit";

    const [title, setTitle]           = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice]           = useState("");
    const [category, setCategory]     = useState(null);
    const [categoryLevels, setCategoryLevels] = useState([]);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [location, setLocation]     = useState(null);
    const [images, setImages]         = useState([]);
    const [loading, setLoading]       = useState(isEditMode);
    const [listing, setListing]       = useState(null);
    const [defaultLocationOptions, setDefaultLocationOptions] = useState([]);
    const imageUrlsRef = useRef([]);

    const { saving, errors, setErrors, submitListing } = useListingUpload({
        api,
        navigate,
        isEditMode,
        listingId: id,
        t,
    });

    const isModerator = profile?.groups?.includes("moderators");
    const isAdmin     = profile?.groups?.includes("admins");
    const isAuthor    = profile?.user?.id === listing?.author?.user?.id;
    const canEdit     = isAuthor || isModerator || isAdmin;
    const descriptionLength = stripHtmlTags(description).trim().length;

    const fetchAllPages = useCallback(async (endpoint, params = {}) => {
        let page = 1;
        const items = [];

        while (true) {
            const res = await api.get(endpoint, { params: { ...params, page } });
            const data = res.data;

            if (Array.isArray(data)) return data;

            items.push(...(data.results || []));

            if (!data.next) break;
            page += 1;
        }

        return items;
    }, [api]);

    const loadCategoryOptionsByParent = useCallback(async (parentId) => {
        const params = { parent: parentId || "root" };
        const categories = await fetchAllPages("categories/", params);

        return categories
            .map((c) => ({
                value: c.id,
                label: c.name,
                childCount: c.child_count || 0,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [fetchAllPages]);

    const buildCategoryPath = useCallback(async (categoryId) => {
        const path = [];
        let currentId = categoryId;
        let guard = 0;

        while (currentId && guard < 10) {
            const res = await api.get(`categories/${currentId}/`);
            path.push(res.data);
            currentId = res.data.parent;
            guard += 1;
        }

        return path.reverse();
    }, [api]);

    useEffect(() => {
        if (!isEditMode || !auth.user?.access_token || !id) {
            return undefined;
        }

        let isMounted = true;

        const fetchListing = async () => {
            setLoading(true);

            try {
                const res = await api.get(`listings/${id}/`);
                const data = res.data;

                if (!isMounted) return;

                setListing(data);
                setTitle(data.title || "");
                setDescription(data.description || "");
                setPrice(data.price || "");

                const locationOption = data.location
                    ? { value: data.location.id, label: data.location.name }
                    : null;

                setLocation(locationOption);
                setDefaultLocationOptions(locationOption ? [locationOption] : []);

                const existingImages = await Promise.all(
                    (data.images || []).map(async (img) => {
                        const imageRes = await fetch(img.url, {
                            headers: { Authorization: `Bearer ${auth.user?.access_token}` },
                        });
                        const blob = await imageRes.blob();
                        const previewUrl = URL.createObjectURL(blob);

                        return {
                            id: `existing-${img.id}`,
                            preview: previewUrl,
                            existing: true,
                            imageId: img.id,
                            objectUrl: previewUrl,
                        };
                    })
                );

                if (!isMounted) {
                    existingImages.forEach((img) => {
                        if (img.objectUrl) URL.revokeObjectURL(img.objectUrl);
                    });
                    return;
                }

                setImages(existingImages);
            } catch (err) {
                console.error(err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchListing();

        return () => {
            isMounted = false;
        };
    }, [isEditMode, auth.user?.access_token, id, api]);

    useEffect(() => {
        imageUrlsRef.current = images
            .map((img) => img.objectUrl)
            .filter(Boolean);
    }, [images]);

    useEffect(() => {
        if (!auth.user?.access_token || (isEditMode && loading)) {
            return undefined;
        }

        let cancelled = false;

        const initializeCategoryLevels = async () => {
            setCategoryLoading(true);

            try {
                const rootOptions = await loadCategoryOptionsByParent(null);
                if (cancelled) return;

                const selectedCategoryId = isEditMode ? listing?.category?.id : null;

                if (!selectedCategoryId) {
                    setCategoryLevels([
                        {
                            parentId: null,
                            options: rootOptions,
                            selected: null,
                        },
                    ]);
                    setCategory(null);
                    return;
                }

                const path = await buildCategoryPath(selectedCategoryId);
                if (cancelled) return;

                if (!path.length) {
                    setCategoryLevels([
                        {
                            parentId: null,
                            options: rootOptions,
                            selected: null,
                        },
                    ]);
                    setCategory(null);
                    return;
                }

                const levels = [
                    {
                        parentId: null,
                        options: rootOptions,
                        selected: null,
                    },
                ];

                for (let depth = 0; depth < path.length; depth += 1) {
                    const node = path[depth];
                    const level = levels[depth];
                    const nodeOption = {
                        value: node.id,
                        label: node.name,
                        childCount: node.child_count || 0,
                    };

                    if (!level.options.some((opt) => opt.value === node.id)) {
                        level.options = [...level.options, nodeOption].sort((a, b) => a.label.localeCompare(b.label));
                    }

                    level.selected = level.options.find((opt) => opt.value === node.id) || nodeOption;

                    if (depth < path.length - 1) {
                        const childOptions = await loadCategoryOptionsByParent(node.id);
                        if (cancelled) return;

                        levels.push({
                            parentId: node.id,
                            options: childOptions,
                            selected: null,
                        });
                    }
                }

                setCategoryLevels(levels);
                setCategory(levels[levels.length - 1]?.selected || null);
            } catch (err) {
                console.error("Failed to initialize category levels:", err);
                if (!cancelled) {
                    setCategoryLevels([
                        {
                            parentId: null,
                            options: [],
                            selected: null,
                        },
                    ]);
                    setCategory(null);
                }
            } finally {
                if (!cancelled) setCategoryLoading(false);
            }
        };

        initializeCategoryLevels();

        return () => {
            cancelled = true;
        };
    }, [
        auth.user?.access_token,
        isEditMode,
        loading,
        listing?.category?.id,
        loadCategoryOptionsByParent,
        buildCategoryPath,
    ]);

    useEffect(() => {
        return () => {
            imageUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    /* ── Dropzone ── */
    const onDrop = useCallback((acceptedFiles) => {
        if (!isEditMode && images.length + acceptedFiles.length > 6) {
            alert(t("listingForm.maxImagesAlert"));
            return;
        }
        setImages((prev) => [
            ...prev,
            ...acceptedFiles.map((file) => {
                const previewUrl = URL.createObjectURL(file);
                return {
                    id: crypto.randomUUID(),
                    file,
                    preview: previewUrl,
                    existing: false,
                    objectUrl: previewUrl,
                };
            }),
        ]);
    }, [images, t, isEditMode]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [] },
    });

    /* ── DnD ── */
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setImages((items) => {
            const oldIndex = items.findIndex((i) => i.id === active.id);
            const newIndex = items.findIndex((i) => i.id === over.id);
            return arrayMove(items, oldIndex, newIndex);
        });
    };

    const removeImage = (id) =>
        setImages((prev) => {
            const img = prev.find((i) => i.id === id);
            if (img?.objectUrl) URL.revokeObjectURL(img.objectUrl);
            return prev.filter((i) => i.id !== id);
        });

    /* ── Async selects ── */
    const loadLocations = async (q) => {
        try {
            const res = await api.get("locations/", { params: { search: q } });
            return res.data.results.map((l) => ({ value: l.id, label: l.name }));
        } catch { return []; }
    };

    const handleCategoryLevelChange = useCallback(async (levelIndex, selectedValue) => {
        const currentLevel = categoryLevels[levelIndex];
        if (!currentLevel) return;

        setErrors((p) => ({ ...p, category: undefined }));

        const selectedOption = currentLevel.options.find((opt) => opt.value === selectedValue) || null;
        const nextLevels = categoryLevels
            .slice(0, levelIndex + 1)
            .map((level, idx) => (idx === levelIndex ? { ...level, selected: selectedOption } : level));

        if (!selectedOption) {
            setCategoryLevels(nextLevels);
            const deepestSelected = [...nextLevels].reverse().find((level) => level.selected)?.selected || null;
            setCategory(deepestSelected);
            return;
        }

        setCategory(selectedOption);
        setCategoryLevels(nextLevels);

        try {
            const childOptions = await loadCategoryOptionsByParent(selectedOption.value);

            setCategoryLevels((prev) => {
                const truncated = prev
                    .slice(0, levelIndex + 1)
                    .map((level, idx) => (idx === levelIndex ? { ...level, selected: selectedOption } : level));

                if (!childOptions.length) {
                    return truncated;
                }

                return [
                    ...truncated,
                    {
                        parentId: selectedOption.value,
                        options: childOptions,
                        selected: null,
                    },
                ];
            });
        } catch (err) {
            console.error("Failed to load subcategories:", err);
        }
    }, [categoryLevels, loadCategoryOptionsByParent, setErrors]);

    /* ── Submit ── */
    const handleSubmit = (event, statusValue) =>
        submitListing({
            event,
            statusValue,
            payload: {
                title,
                description,
                price,
                category,
                location,
                images,
            },
        });

    if (!auth.isAuthenticated) return (
        <div className={styles.stateCenter}>{t("listingForm.loginRequired")}</div>
    );

    if (isEditMode && loading) return (
        <div className={styles.stateCenter}>
            <div className={styles.spinner} />
            <p>{t("listingEdit.loading")}</p>
        </div>
    );

    if (isEditMode && !canEdit) return (
        <div className={styles.stateCenter}>{t("listingEdit.notAllowed")}</div>
    );

    return (
        <div className={styles.container}>
            <form className={styles.form} onSubmit={(e) => e.preventDefault()}>

                <div className={styles.formHeader}>
                    <h2 className={styles.formTitle}>
                        {isEditMode ? t("listingEdit.title") : t("listingForm.title")}
                    </h2>
                    <p className={styles.formSubtitle}>
                        {isEditMode ? t("listingEdit.subtitle") : t("listingForm.subtitle")}
                    </p>
                </div>

                {/* Title */}
                <Field label={t("listingForm.titleField")} error={errors.title?.[0]}>
                    <input
                        value={title}
                        onChange={(e) => {
                            setTitle(clampToMaxLength(e.target.value, FIELD_LIMITS.listingTitle));
                            setErrors((p) => ({ ...p, title: undefined }));
                        }}
                        className={`${styles.input} ${errors.title ? styles.inputError : ""}`}
                        placeholder={t("listingForm.titlePlaceholder")}
                        maxLength={FIELD_LIMITS.listingTitle}
                    />
                </Field>

                {/* Description */}
                <Field label={t("listingForm.descriptionField")} error={errors.description?.[0]}>
                    <RichTextEditor
                        value={description}
                        onChange={(value) => {
                            setDescription(value);
                            setErrors((p) => ({ ...p, description: undefined }));
                        }}
                    />
                    <span className={`${styles.charCount} ${descriptionLength > FIELD_LIMITS.listingDescription ? styles.charCountError : ""}`}>
                        {t("listingForm.charactersCount", { count: descriptionLength, max: FIELD_LIMITS.listingDescription })}
                    </span>
                </Field>

                {/* Price */}
                <Field label={t("listingForm.priceField")} error={errors.price?.[0]}>
                    <div className={styles.priceWrapper}>
                        <span className={styles.priceSymbol}>€</span>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={price}
                            onChange={(e) => {
                                setPrice(e.target.value);
                                setErrors((p) => ({ ...p, price: undefined }));
                            }}
                            className={`${styles.input} ${styles.priceInput} ${errors.price ? styles.inputError : ""}`}
                            placeholder="0.00"
                        />
                    </div>
                </Field>

                {/* Category & Location */}
                <div className={styles.row}>
                    <Field label={t("listingForm.categoryField")} error={errors.category?.[0]}>
                        <div className={styles.categorySelectStack}>
                            {categoryLevels.map((level, index) => (
                                <select
                                    key={`${level.parentId || "root"}-${index}`}
                                    value={level.selected?.value || ""}
                                    onChange={(e) => handleCategoryLevelChange(index, e.target.value || null)}
                                    className={`${styles.input} ${errors.category ? styles.inputError : ""}`}
                                    disabled={categoryLoading}
                                >
                                    <option value="">
                                        {index === 0
                                            ? t("listingForm.selectRootCategory")
                                            : t("listingForm.selectSubCategory", { level: index })}
                                    </option>
                                    {level.options.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            ))}
                            {categoryLoading && (
                                <p className={styles.categoryHelp}>{t("listingForm.loadingCategories")}</p>
                            )}
                            {category && (
                                <p className={styles.categoryHelp}>
                                    {t("listingForm.selectedCategoryPath", {
                                        path: categoryLevels
                                            .map((level) => level.selected?.label)
                                            .filter(Boolean)
                                            .join(" / "),
                                    })}
                                </p>
                            )}
                        </div>
                    </Field>
                    <Field label={t("listingForm.locationField")} error={errors.location?.[0]}>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions={isEditMode ? defaultLocationOptions : true}
                            loadOptions={loadLocations}
                            value={location}
                            onChange={(value) => {
                                setLocation(value);
                                setErrors((p) => ({ ...p, location: undefined }));
                            }}
                            placeholder={t("listingForm.selectLocation")}
                            isClearable
                            classNamePrefix="react-select"
                        />
                    </Field>
                </div>

                {/* Dropzone */}
                <Field label={t("listingForm.photosCount", { count: images.length })}>
                    <div
                        {...getRootProps()}
                        className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ""}`}
                    >
                        <input {...getInputProps()} />
                        <span className={styles.dropzoneIcon}>🖼</span>
                        <p className={styles.dropzoneText}>
                            {isDragActive ? t("listingForm.dropHere") : t("listingForm.dropPrompt")}
                        </p>
                        <p className={styles.dropzoneHint}>{t("listingForm.dropHint")}</p>
                    </div>
                </Field>

                {/* Image grid */}
                {images.length > 0 && (
                    <div>
                        <p className={styles.imageHint}>{t("listingForm.imageHint")}</p>
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={images.map((i) => i.id)} strategy={horizontalListSortingStrategy}>
                                <div className={styles.imageGrid}>
                                    {images.map((img, i) => (
                                        <SortableImage key={img.id} image={img} index={i} onRemove={removeImage} t={t} />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                )}

                {/* Action buttons */}
                <div className={styles.buttonRow}>
                    <button
                        type="button"
                        disabled={saving}
                        onClick={(e) => handleSubmit(e, "draft")}
                        className={styles.draftBtn}
                    >
                        {saving ? t("common.saving") : t("listingForm.saveDraft")}
                    </button>
                    <button
                        type="button"
                        disabled={saving}
                        onClick={(e) => handleSubmit(e, "pend_app")}
                        className={styles.submitBtn}
                    >
                        {isEditMode
                            ? (saving ? t("common.saving") : t("listingForm.submitApproval"))
                            : (saving ? t("listingForm.submitting") : t("listingForm.submitApproval"))}
                    </button>
                    {(isModerator || isAdmin) && isEditMode && (
                        <button
                            type="button"
                            disabled={saving}
                            onClick={(e) => handleSubmit(e, "rejected")}
                            className={styles.rejectBtn}
                        >
                            {saving ? t("common.saving") : `✕ ${t("listingEdit.reject")}`}
                        </button>
                    )}
                    {(isModerator || isAdmin) ? (
                        <button
                            type="button"
                            disabled={saving}
                            onClick={(e) => handleSubmit(e, "active")}
                            className={styles.approveBtn}
                        >
                            {isEditMode
                                ? (saving ? t("common.saving") : `✓ ${t("listingEdit.approve")}`)
                                : t("listingForm.publishDirectly")}
                        </button>
                    ) : null}
                </div>

            </form>
        </div>
    );
}
